import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api";

interface Notification {
  id: string;
  site_name: string;
  work_type: string;
  work_date: string;
  worker_count: number;
  work_hours: number;
}

const NOTIFICATION_LIMIT = 10;
const SEEN_KEY = "ugongisan_seen_notifications";

function getSeenIds(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveSeenIds(ids: Set<string>) {
  try {
    const recent = Array.from(ids).slice(-200);
    localStorage.setItem(SEEN_KEY, JSON.stringify(recent));
  } catch {
    // localStorage full or unavailable, ignore
  }
}

export default function Navbar() {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Notification state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [seenIds, setSeenIds] = useState<Set<string>>(getSeenIds);
  const notifRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter(n => !seenIds.has(n.id)).length;

  const markAllSeen = useCallback(() => {
    const allIds = new Set(notifications.map(n => n.id));
    const merged = new Set([...seenIds, ...allIds]);
    setSeenIds(merged);
    saveSeenIds(merged);
  }, [notifications, seenIds]);

  const handleBellClick = () => {
    if (!notifOpen) {
      markAllSeen();
    }
    setNotifOpen(prev => !prev);
    setMenuOpen(false);
  };

  useEffect(() => {
    async function fetchNotifications() {
      try {
        const data = await api.get<Notification[]>("/reports");
        setNotifications(data.slice(0, NOTIFICATION_LIMIT));
      } catch {
        // silently ignore fetch errors for notifications
      }
    }

    fetchNotifications();
  }, []);

  // Close notif dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    await signOut();
    navigate("/login");
  };

  const roleLabel: Record<string, string> = {
    worker: "작업자",
    supervisor: "반장",
    manager: "현장소장",
    safety: "안전관리자",
  };

  const workTypeBadge: Record<string, string> = {
    "칩핑": "bg-amber-100 text-amber-700",
    "파쇄": "bg-orange-100 text-orange-700",
    "상차": "bg-emerald-100 text-emerald-700",
    "정리": "bg-sky-100 text-sky-700",
    "철거": "bg-rose-100 text-rose-700",
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-100 z-50 flex items-center justify-between px-6">
      <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
        <img
          src="https://storage.readdy-site.link/project_files/e75414f1-63e2-454f-b8ec-8b79491ecbbd/45a9a30b-a23e-496a-85df-ee9dd902d79f_compressed_1.webp"
          alt="우공이산"
          className="h-10 w-auto max-w-[200px] object-contain"
        />
        <p className="text-[11px] text-gray-400">UGONGISAN</p>
      </div>

      <div className="flex items-center gap-4">
        {/* Notification Bell */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={handleBellClick}
            className="relative w-9 h-9 rounded-lg hover:bg-gray-50 flex items-center justify-center cursor-pointer transition-colors"
          >
            <i className={`${notifOpen ? "ri-notification-3-fill" : "ri-notification-3-line"} text-gray-500 text-lg`}></i>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {notifOpen && (
            <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                <p className="text-sm font-semibold text-gray-900">알림</p>
                {unreadCount > 0 && (
                  <span className="text-xs text-amber-600 font-medium">{unreadCount}개의 새로운 알림</span>
                )}
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 px-4">
                    <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                      <i className="ri-notification-off-line text-gray-300 text-xl"></i>
                    </div>
                    <p className="text-sm text-gray-400">아직 알림이 없습니다</p>
                    <p className="text-xs text-gray-300 mt-0.5">새로운 작업일보가 등록되면 여기에 표시됩니다</p>
                  </div>
                ) : (
                  notifications.map((n, idx) => {
                    const isUnread = !seenIds.has(n.id);
                    return (
                      <div
                        key={n.id}
                        className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                          idx !== notifications.length - 1 ? "border-b border-gray-50" : ""
                        } ${isUnread ? "bg-amber-50/40" : ""}`}
                        onClick={() => navigate("/reports")}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isUnread ? "bg-amber-100" : "bg-gray-100"
                        }`}>
                          <i className={`ri-file-list-3-line text-sm ${isUnread ? "text-amber-600" : "text-gray-400"}`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <p className="text-sm font-medium text-gray-900 truncate">{n.site_name}</p>
                            {isUnread && (
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"></span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${workTypeBadge[n.work_type] || "bg-gray-100 text-gray-600"}`}>
                              {n.work_type}
                            </span>
                            <span className="text-xs text-gray-400 whitespace-nowrap">{n.worker_count}명 · {n.work_hours}h</span>
                          </div>
                          <p className="text-[11px] text-gray-400 mt-0.5">{n.work_date}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {notifications.length > 0 && (
                <div className="border-t border-gray-50 px-4 py-2.5">
                  <button
                    onClick={() => { setNotifOpen(false); navigate("/reports"); }}
                    className="w-full text-center text-xs text-amber-600 font-medium hover:text-amber-700 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    모든 작업일보 보기
                    <i className="ri-arrow-right-line ml-1 text-[10px]"></i>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => { setMenuOpen(!menuOpen); setNotifOpen(false); }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap"
          >
            <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-3-fill text-amber-600 text-xs"></i>
            </div>
            <span className="text-sm text-gray-600">{profile?.name || "사용자"}</span>
            <i className="ri-arrow-down-s-line text-gray-400 text-sm"></i>
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
              <div className="px-4 py-2 border-b border-gray-50">
                <p className="text-sm font-medium text-gray-900">{profile?.name || "사용자"}</p>
                <p className="text-xs text-gray-400">{roleLabel[profile?.role || "worker"]}</p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-logout-box-line text-base"></i>
                로그아웃
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}